import React, { useEffect, useState } from "react";
import { Table, Thead, Tbody, Tr, Th, Td, Spinner, useTheme, Box, Input, IconButton, InputGroup, InputRightElement, Tooltip, Button, useColorModeValue, Text, HStack, Tag, TagLabel, TagLeftIcon, Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { SearchIcon, CloseIcon, ChevronDownIcon, InfoIcon } from "@chakra-ui/icons";
import { FaFilter } from "react-icons/fa"; // Using FaFilter from react-icons
import axios from "axios";
import Exports from "./Exports";

const Table1 = ({ supplier, retailer }) => {
  const theme = useTheme();

  // Move all useColorModeValue hooks to the top level
  const tableBg = useColorModeValue("white", "gray.800");
  const headerBg = useColorModeValue("gray.100", "gray.700");
  const headerActiveBg = useColorModeValue("primary1", "darkPrimary1");
  const headerActiveColor = useColorModeValue("white", "white");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const stickyBg = useColorModeValue("rgba(237, 242, 247, 0.9)", "rgba(45, 55, 72, 0.9)");
  const stickyBgHover = useColorModeValue("rgba(237, 242, 247, 1)", "rgba(45, 55, 72, 1)");
  const highlightColor = useColorModeValue("primary2", "darkPrimary2");
  const stripeBg = useColorModeValue("rgba(237, 242, 247, 0.9)", "gray.700");
  const searchBg = useColorModeValue("white", "gray.700");
  const bgColor = useColorModeValue("gray.50", "gray.700"); // Move this hook call to the top level

  const [thead, setThead] = useState([]);
  const [tbody, setTbody] = useState([]);
  const [nrItems, setNrItems] = useState(0);
  const [brands, setBrands] = useState([]);
  const [filteredTbody, setFilteredTbody] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);

  // useEffect(() => {
  //   const buildTable = async () => {
  //     setLoading(true);
  //     setFilteredTbody([]);
  //     setTbody([]);
  //     setThead([]);
  //     try {
  //       const res = await axios.post(`${process.env.REACT_APP_API_URL}/rep_sales/table1`, {
  //         supplier,
  //         retailer,
  //         userid: localStorage.getItem("userid"),
  //       });
  //       setThead(res.data.data.tableData.thead);
  //       setTbody(res.data.data.tableData.tbody);
  //       setFilteredTbody(res.data.data.tableData.tbody);
  //       setLoading(false);
  //     } catch (error) {
  //       console.error("Error fetching table data:", error);
  //       setLoading(false);
  //     }
  //   };
  //   buildTable();
  // }, [supplier, retailer]);
  useEffect(() => {
    const controller = new AbortController();
    const buildTable = async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/rep_sales/table1`, { supplier, retailer, userid: localStorage.getItem("userid") }, { signal: controller.signal });
        setThead(data.data.tableData.thead);
        setTbody(data.data.tableData.tbody);
        setFilteredTbody(data.data.tableData.tbody);
        setNrItems(data.data.nr_items);
        setBrands(data.data.brands);
      } catch (err) {
        if (!axios.isCancel(err)) console.error("table1 fetch failed", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    buildTable();
    return () => controller.abort(); // ⇦ cancels any in-flight request
  }, [supplier, retailer]);

  const handleSearch = () => {
    const lowercasedFilter = searchText.toLowerCase();
    const filteredData = tbody.filter((item) => {
      return item.some((col, index) => {
        if (index === 1 || index === 2 || index === 3) {
          const colData = Array.isArray(col) ? col[0] : col;
          return colData.toString().toLowerCase().includes(lowercasedFilter);
        }
        return false;
      });
    });
    setFilteredTbody(filteredData);
  };

  const handleClearSearch = () => {
    setSearchText("");
    setFilteredTbody(tbody);
  };

  useEffect(() => {
    if (searchText) {
      setShowTooltip(true);
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShowTooltip(false);
    }
  }, [searchText]);

  const getAttributes = (attributes) => {
    const attrObj = {};
    if (Array.isArray(attributes)) {
      attributes.forEach((attr) => {
        if (attr.key === "class") {
          Object.assign(attrObj, mapClassNameToChakraProps(attr.value));
        } else if (attr.key === "colspan") {
          attrObj.colSpan = attr.value;
        } else if (attr.key === "rowspan") {
          attrObj.rowSpan = attr.value;
        } else {
          attrObj[attr.key] = attr.value;
        }
      });
    } else {
      if (attributes.key === "class") {
        Object.assign(attrObj, mapClassNameToChakraProps(attributes.value));
      } else if (attributes.key === "colspan") {
        attrObj.colSpan = attributes.value;
      } else if (attributes.key === "rowspan") {
        attrObj.rowSpan = attributes.value;
      } else {
        attrObj[attributes.key] = attributes.value;
      }
    }

    return attrObj;
  };

  const mapClassNameToChakraProps = (className) => {
    switch (className) {
      case "sdc":
        return {
          bg: "rgba(219, 219, 219, 0.5) !important",
          _hover: { bg: "rgba(219, 219, 219, 0.7)" },
        };
      case "sdcDC":
        return {
          bg: "rgba(242, 242, 242, 0.5) !important",
          _hover: { bg: "rgba(242, 242, 242, 0.7)" },
        };
      case "status4":
        return {
          bg: "rgba(219, 219, 219, 0.5) !important",
          _hover: { bg: "rgba(219, 219, 219, 0.7)" },
        };
      case "psc":
        return {
          bg: "rgba(242, 242, 242, 0.5) !important",
          _hover: { bg: "rgba(242, 242, 242, 0.7)" },
        };
      case "yoy1":
        return {
          bg: "rgba(219, 219, 219, 0.5) !important",
          _hover: { bg: "rgba(219, 219, 219, 0.7)" },
        };
      case "yoy2":
        return {
          bg: "rgba(242, 242, 242, 0.5) !important",
          _hover: { bg: "rgba(242, 242, 242, 0.7)" },
        };
      case "yoy3":
        return {
          bg: "rgba(219, 219, 219, 0.5) !important",
          _hover: { bg: "rgba(219, 219, 219, 0.7)" },
        };
      case "table1_sticky1":
        return {
          position: "sticky",
          left: "0",
          zIndex: "2",
          bg: "gray.400 ",
          _hover: { bg: stickyBgHover },
          boxShadow: "1px 0 3px rgba(0,0,0,0.1)",
          transition: "all 0.2s ease",
        };
      case "table1_sticky2":
        return {
          position: "sticky",
          left: "60px",
          zIndex: "2",
          bg: "gray.200 ",
          _hover: { bg: stickyBgHover },
          boxShadow: "1px 0 3px rgba(0,0,0,0.1)",
          transition: "all 0.2s ease",
        };
      case "brand_totals":
        return {
          textAlign: "right",
          fontWeight: "bold",
          bg: "rgba(88,182,182,0.2) !important",
          _hover: { bg: "rgba(88,182,182,0.3)" },
        };
      case "th3":
        return {
          bg: "rgba(88,182,182,0.2) !important",
          _hover: { bg: "rgba(88,182,182,0.3)" },
        };
      case "blank":
        return {
          bg: "rgba(255,255,255,1) !important",
        };
      case "th4":
        return {
          bg: "rgba(88,182,182,0.2)",
          _hover: { bg: "rgba(88,182,182,0.3) !important" },
        };
      case "forcastCell":
        return {
          color: `white !important`,
          bg: "rgba(243, 115, 53, 0.8) !important",
          _hover: { bg: "rgba(243, 115, 53, 0.9)" },
          fontWeight: "medium",
        };
      default:
        return {};
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" thickness="4px" speed="0.65s" color={highlightColor} />
        <Text mt={4} fontWeight="medium">
          Loading sales data...
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        mb={4}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        bg={bgColor} // Use the pre-defined value here
        p={3}
        borderRadius="md"
      >
        <Box>
          <Exports supplier={supplier} retailer={retailer} brands={brands} />
        </Box>

        <HStack spacing={1}>
          <Menu closeOnSelect={true}>
            {/* <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="sm" variant="outline" colorScheme="gray">
              <HStack>
                <FaFilter /> 
                <Text>Filter</Text>
              </HStack>
            </MenuButton> */}

            <MenuList>
              <MenuItem>Show All</MenuItem>
              <MenuItem>Show Only Positive Growth</MenuItem>
              <MenuItem>Show Only Negative Growth</MenuItem>
              <MenuItem>Show Top 10 Products</MenuItem>
            </MenuList>
          </Menu>

          <InputGroup size="sm" width="300px">
            <Tooltip label="Press Enter to Search and Esc to clear" isOpen={showTooltip} placement="top" hasArrow>
              <Input
                pr="4.5rem"
                type="text"
                placeholder="Search by product name or code..."
                value={searchText}
                bg={searchBg}
                borderRadius="md"
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  } else if (e.key === "Escape") {
                    handleClearSearch();
                  }
                }}
              />
            </Tooltip>
            <InputRightElement width="4.5rem">
              {searchText && <IconButton h="1.5rem" size="xs" onClick={handleClearSearch} icon={<CloseIcon />} mr={1} variant="ghost" />}
              <IconButton h="1.5rem" size="xs" onClick={handleSearch} icon={<SearchIcon />} colorScheme="blue" />
            </InputRightElement>
          </InputGroup>
        </HStack>

        <Tag size="sm" variant="subtle" colorScheme="cyan">
          <TagLeftIcon as={InfoIcon} />
          <TagLabel>{nrItems} items</TagLabel>
        </Tag>
      </Box>

      <Box
        borderRadius="md"
        overflowX="auto"
        overflowY="auto"
        boxShadow="sm"
        maxW="100%"
        maxH="400px"
        borderWidth="1px"
        borderColor={borderColor}
        className="table-container"
        sx={{
          ".table-container::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          ".table-container::-webkit-scrollbar-track": {
            width: "10px",
            height: "10px",
          },
          ".table-container::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.2)",
            borderRadius: "24px",
          },
        }}
      >
        <Table
          variant="simple"
          size="sm"
          bg={tableBg}
          sx={{
            // Make table more compact
            "th, td": {
              py: "0.25rem",
              px: "0.5rem",
              fontSize: "2xs",
              lineHeight: "shorter",
            },
            "thead th": {
              position: "sticky",
              top: 0,
              zIndex: 4,
              bg: stickyBg /* keep the colour even when scrolled */,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            },
            // Add subtle hover effect on rows
            "tbody tr": {
              _hover: {
                bg: hoverBg,
              },
            },
            "tbody tr:nth-of-type(even)": {
              bg: stripeBg,
              _hover: { bg: hoverBg },
            },
            // Better styling for sticky header
            thead: {
              position: "sticky",
              top: 0,
              zIndex: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            },
            // Improve number alignment
            "td:not(:nth-of-type(1)):not(:nth-of-type(2)):not(:nth-of-type(3))": {
              textAlign: "right",
            },
          }}
        >
          <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
            {thead.map((row, rowIndex) => (
              <Tr key={`thead-row-${rowIndex}`}>
                {row.map((col, colIndex) => {
                  const colData = Array.isArray(col) ? col[0] : col;
                  const attributes = Array.isArray(col) && col[1] ? getAttributes(col[1]) : {};

                  return (
                    <Th key={`thead-cell-${rowIndex}-${colIndex}`} {...attributes} p={1} fontSize="xs" whiteSpace="nowrap" textAlign="center" borderRight="1px" borderColor={borderColor} fontWeight="semibold" textTransform="uppercase" letterSpacing="0.5px" color="gray.600" _dark={{ color: "gray.300" }}>
                      {colData}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {filteredTbody.map((row, rowIndex) => (
              <Tr key={`tbody-row-${rowIndex}`} bg={rowIndex % 2 === 1 ? stripeBg : undefined} _hover={{ bg: hoverBg }}>
                {row.map((col, colIndex) => {
                  const colData = Array.isArray(col) ? col[0] : col;
                  const attributes = Array.isArray(col) && col[1] ? getAttributes(col[1]) : {};

                  // Check if this is a numeric cell that should be formatted
                  const isNumeric = colIndex > 2 && !isNaN(Number.parseFloat(colData));
                  const isPercentage = isNumeric && String(colData).includes("%");
                  const isNegative = isNumeric && Number.parseFloat(colData) < 0;

                  return (
                    <Td
                      key={`tbody-cell-${rowIndex}-${colIndex}`}
                      {...attributes}
                      p={1}
                      fontSize="xs"
                      // bg={
                      //   attributes.position === "sticky" //
                      //     ? rowIndex % 2 === 1 //
                      //       ? stripeBg // use the same zebra colour
                      //       : stickyBg // keep default for the other rows
                      //     : undefined // non-sticky cells just inherit
                      // }
                      whiteSpace="nowrap"
                      borderRight="1px"
                      borderColor={borderColor}
                      color={isNegative ? "red.500" : undefined}
                      fontWeight={isPercentage ? "medium" : "normal"}
                    >
                      {colData}
                    </Td>
                  );
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default Table1;
